// src/components/Header.jsx
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

function Header() {
  const [activeSection, setActiveSection] = useState('');
  const [isPublicationsDropdownOpen, setIsPublicationsDropdownOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false); // NEW: Mobile menu toggle
  const location = useLocation();
  const navigate = useNavigate();

  // Handle scroll highlighting
  useEffect(() => {
    const handleScroll = () => {
      if (location.pathname === '/') {
        const sections = ['hero', 'about', 'projects-section', 'contact'];
        const offset = 100;

        let currentActive = '';
        for (let i = sections.length - 1; i >= 0; i--) {
          const section = document.getElementById(sections[i]);
          if (section) {
            if (window.pageYOffset >= section.offsetTop - offset) {
              currentActive = sections[i];
              break;
            }
          }
        }
        setActiveSection(currentActive);
      } else {
        setActiveSection('');
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [location.pathname]);

  const handleNavLinkClick = (sectionId) => (e) => {
    e.preventDefault();

    if (location.pathname === '/') {
      const section = document.getElementById(sectionId);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate('/', { state: { scrollToId: sectionId } });
    }
    setIsPublicationsDropdownOpen(false);
    setIsMenuOpen(false); // Close mobile menu on link click
  };

  const togglePublicationsDropdown = () => {
    setIsPublicationsDropdownOpen((prevState) => !prevState);
  };

  // Close dropdown if clicked outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isPublicationsDropdownOpen &&
        !event.target.closest('#publications-dropdown-container')
      ) {
        setIsPublicationsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isPublicationsDropdownOpen]);

  return (
    <header className="w-full py-4 bg-olive-accent text-white shadow-md sticky top-0 z-10">
      <div className="container mx-auto flex justify-between items-center px-6">
        
        {/* Logo */}
        <Link
          to="/"
          className="flex items-center space-x-3 text-2xl font-bold text-white"
        >
          <img
            src={`${process.env.PUBLIC_URL}/images/rai_logo.png`}
            alt="RAI Lab Logo"
            className="h-10 w-auto"
          />
          <span>RAI Lab</span>
        </Link>

        {/* Mobile Hamburger Button */}
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="md:hidden text-white focus:outline-none"
        >
          {/* Simple hamburger icon */}
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            {isMenuOpen ? (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            ) : (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 6h16M4 12h16M4 18h16"
              />
            )}
          </svg>
        </button>

        {/* Nav Links */}
        <nav
          className={`${
            isMenuOpen ? 'block' : 'hidden'
          } md:block absolute md:static top-full left-0 w-full md:w-auto bg-olive-accent md:bg-transparent`}
        >
          <ul className="flex flex-col space-y-4 md:flex-row md:space-x-8 md:space-y-0 p-4 md:p-0">
            <li>
              <a
                href="#about"
                onClick={handleNavLinkClick('about')}
                className={`nav-link ${
                  activeSection === 'about' && location.pathname === '/'
                    ? 'active'
                    : ''
                }`}
              >
                About
              </a>
            </li>
            <li>
              <Link
                to="/projects-grants"
                onClick={() => setIsMenuOpen(false)}
                className={`nav-link ${
                  location.pathname === '/projects-grants' ? 'active' : ''
                }`}
              >
                Projects
              </Link>
            </li>

            {/* Publications Dropdown */}
            <li className="relative" id="publications-dropdown-container">
              <button
                onClick={togglePublicationsDropdown}
                className={`nav-link focus:outline-none ${
                  location.pathname.startsWith('/publications') ? 'active' : ''
                }`}
              >
                Publications <span className="ml-1">&#9662;</span>
              </button>
              {isPublicationsDropdownOpen && (
                <ul className="absolute md:left-0 mt-2 w-full md:w-48 bg-white text-gray-800 rounded-md shadow-lg py-2 z-20 md:static md:mt-0">
                  <li>
                    <Link
                      to="/publications/journal"
                      onClick={() => {
                        setIsPublicationsDropdownOpen(false);
                        setIsMenuOpen(false);
                      }}
                      className="block px-4 py-2 hover:bg-gray-100"
                    >
                      Journal Publications
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/publications/conference"
                      onClick={() => {
                        setIsPublicationsDropdownOpen(false);
                        setIsMenuOpen(false);
                      }}
                      className="block px-4 py-2 hover:bg-gray-100"
                    >
                      Conference Publications
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/publications/poster"
                      onClick={() => {
                        setIsPublicationsDropdownOpen(false);
                        setIsMenuOpen(false);
                      }}
                      className="block px-4 py-2 hover:bg-gray-100"
                    >
                      Poster Presentations
                    </Link>
                  </li>
                </ul>
              )}
            </li>

            <li>
              <Link
                to="/news"
                onClick={() => setIsMenuOpen(false)}
                className={`nav-link ${
                  location.pathname === '/news' ? 'active' : ''
                }`}
              >
                News
              </Link>
            </li>
            {/* <li>
              <Link
                to="/team"
               onClick={() => setIsMenuOpen(false)}
               className={`nav-link ${
               location.pathname === '/team' ? 'active' : ''
               }`}
             >
                Team
              </Link>
            </li> */}
          </ul>
        </nav>
      </div>
    </header>
  );
}

export default Header;
